import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import type { UndefinedOnPartialDeep } from 'type-fest';
import { useShallow } from 'zustand/shallow';

import { useTimeWindowUpdateStore } from '@/store/time-window-update.store';
import type { Service } from '@repo/types/service';
import { computeDayKey } from '@repo/utils';

import { getServiceTimeWindowStartUtc, getServiceTimeZone } from '@/utils/services.utils';
import { getAppConfig } from '@/utils/window.utils';

import { useTimeWindowCandidates } from './time-window-candidates.hooks';
import { getAvailableTimeWindowDates, getInitialMonthSelection, getMonthStartFromDate } from './time-window.utils';

interface ActiveMonthState {
    serviceId: string | undefined;
    date: dayjs.Dayjs;
}

export const useTimeWindowSelection = (service: UndefinedOnPartialDeep<Service>) => {
    const appConfig = getAppConfig();
    const serviceTimeZone = getServiceTimeZone(service);
    const currentTimeWindow = service.timeWindow;
    const currentTimeWindowId = currentTimeWindow?.timeWindowId;
    const serviceTimeWindowStartUtc = getServiceTimeWindowStartUtc(service);
    const serviceMonthStartDate = serviceTimeWindowStartUtc.startOf('month');

    const [activeMonth, setActiveMonth] = useState<ActiveMonthState>(() => ({
        serviceId: service.serviceId,
        date: serviceMonthStartDate,
    }));

    const activeMonthStartDate =
        activeMonth.serviceId === service.serviceId ? activeMonth.date.startOf('month') : serviceMonthStartDate;

    const { selectedDayKey, selectedTimeWindowId, setSelectedDayKey, setSelectedTimeWindowId } =
        useTimeWindowUpdateStore(
            useShallow((state) => ({
                selectedDayKey: state.selectedDayKey,
                selectedTimeWindowId: state.selectedTimeWindowId,
                setSelectedDayKey: state.setSelectedDayKey,
                setSelectedTimeWindowId: state.setSelectedTimeWindowId,
            }))
        );
    const { orderData, timeWindows, isError, isFetching, hasServicePriceError, timeWindowItems } =
        useTimeWindowCandidates({
            service,
            activeMonthStartDate,
            selectedDayKey,
            selectedTimeWindowId,
        });
    const availableTimeWindowDates = useMemo(
        () => getAvailableTimeWindowDates(timeWindows, serviceTimeZone),
        [serviceTimeZone, timeWindows]
    );
    const currentDeliveryDayKey = computeDayKey(serviceTimeWindowStartUtc.toISOString(), serviceTimeZone);
    const selectedDate =
        selectedDayKey === currentDeliveryDayKey && currentTimeWindow
            ? serviceTimeWindowStartUtc.toDate()
            : selectedDayKey
              ? dayjs.utc(selectedDayKey).toDate()
              : undefined;
    const hasDisplayedTimeWindows = timeWindowItems.length > 0;
    const shouldShowTimeWindowList = Boolean(selectedDate && hasDisplayedTimeWindows);
    const shouldShowEmptyMessage = Boolean(selectedDayKey) && !hasDisplayedTimeWindows && !isFetching;
    const activeMonthStartKey = activeMonthStartDate.toISOString();
    const serviceTimeWindowStartKey = serviceTimeWindowStartUtc.toISOString();
    const availableTimeWindowDatesKey = availableTimeWindowDates.map((date) => date.toISOString()).join('|');

    useEffect(() => {
        const availableDatesForInitialSelection =
            availableTimeWindowDatesKey === ''
                ? []
                : availableTimeWindowDatesKey.split('|').map((date) => dayjs.utc(date));

        const initialSelection = getInitialMonthSelection({
            activeMonthStartDate: dayjs.utc(activeMonthStartKey),
            availableDates: availableDatesForInitialSelection,
            currentDeliveryDateUtc: dayjs.utc(serviceTimeWindowStartKey),
            currentTimeWindowId: currentTimeWindowId ?? '',
            timeZone: serviceTimeZone,
        });

        setSelectedDayKey(initialSelection.selectedDayKey);
        setSelectedTimeWindowId(initialSelection.timeWindowId);
    }, [
        activeMonthStartKey,
        currentTimeWindowId,
        serviceTimeWindowStartKey,
        serviceTimeZone,
        setSelectedDayKey,
        setSelectedTimeWindowId,
        availableTimeWindowDatesKey,
    ]);

    const handleDayChange = (date: Date) => {
        const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        if (selectedDayKey === dayKey) {
            return;
        }

        setSelectedDayKey(dayKey);

        if (dayKey === currentDeliveryDayKey) {
            setSelectedTimeWindowId(currentTimeWindowId);
            return;
        }

        setSelectedTimeWindowId(undefined);
    };

    const handleMonthChange = (date: Date) => {
        const nextMonthStartDate = getMonthStartFromDate(date);

        if (nextMonthStartDate.isSame(activeMonthStartDate, 'month')) {
            return;
        }

        setActiveMonth({
            serviceId: service.serviceId,
            date: nextMonthStartDate,
        });
    };

    const todayMonthStartDate = dayjs.utc().startOf('month');
    const calendarStartMonth = serviceMonthStartDate.isBefore(todayMonthStartDate, 'month')
        ? serviceMonthStartDate
        : todayMonthStartDate;

    return {
        availableDates: availableTimeWindowDates.map((date) => date.toDate()),
        currentDate: serviceTimeWindowStartUtc.toDate(),
        dateFormat: appConfig.dateFormat,
        handleDayChange,
        handleMonthChange,
        handleTimeWindowSelect: setSelectedTimeWindowId,
        hasOrderData: Boolean(orderData),
        hasServicePriceError,
        initialDate: selectedDate ?? serviceTimeWindowStartUtc.toDate(),
        isError,
        isFetching,
        selectedDate,
        selectedTimeWindowId,
        shouldShowEmptyMessage,
        shouldShowTimeWindowList,
        startMonth: calendarStartMonth.toDate(),
        timeFormat: appConfig.timeFormat,
        timeWindowItems,
        timeZone: currentTimeWindow?.timeZone,
        weekStartsOn: appConfig.weekStartsOn ?? 1,
    };
};
